import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["backdrop", "className", "dialog", "globalStyle", "isOpen", "portal"];
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Dialog } from '../Dialog/Dialog';
import { Mask } from '../Mask/Mask';
import { ModalGlobalStyle, StyledModal } from './Modal.styles';

/**
 * The `Modal` component provides the base for modal dialogs such as
 * `ModalDialog` and `Popover`.
 *
 * Non-documented props will be passed to the dialog node.
 *
 * @related ['Dialog', 'Mask', 'Popover']
 * @since 7.0.0
 */
export var Modal = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var backdrop = _ref.backdrop,
    className = _ref.className,
    dialog = _ref.dialog,
    globalStyle = _ref.globalStyle,
    isOpen = _ref.isOpen,
    portal = _ref.portal,
    rest = _objectWithoutProperties(_ref, _excluded);
  // Do not render anything while closed
  if (!isOpen) {
    return null;
  }

  // Pass same dialog props to global style node
  var globalStyleContent = /*#__PURE__*/cloneElement(globalStyle, Object.assign({}, rest, {
    children: undefined
  }));

  // Don't portal dialog since we are handling portal at the modal level
  var dialogContent = /*#__PURE__*/cloneElement(dialog, Object.assign({
    portal: false
  }, rest));
  var content = /*#__PURE__*/React.createElement(StyledModal, {
    className: className,
    ref: forwardedRef
  }, globalStyleContent, backdrop, dialogContent);
  if (portal === true && typeof document !== 'undefined') {
    return /*#__PURE__*/createPortal(content, document.body);
  }
  if (portal !== true && portal) {
    return /*#__PURE__*/createPortal(content, portal);
  }
  return content;
});
Modal.displayName = 'Modal';
Modal.propTypes = {
  /**
   * The backdrop node is a sibling of the `dialog` node used to conceal the content behind the
   * dialog.
   */
  backdrop: PropTypes.node,
  /**
   * The class to be passed to the modal.
   */
  className: PropTypes.string,
  /**
   * The Dialog component.
   */
  dialog: PropTypes.node,
  /**
   * The styled-components
   * [global style](https://www.styled-components.com/docs/api#createglobalstyle) node.
   */
  globalStyle: PropTypes.node,
  /**
   * The open state of the modal.
   */
  isOpen: PropTypes.bool,
  /**
   * By default, content will be rendered into a [Portal](https://reactjs.org/docs/portals.html)
   * in the document body. With this prop, you can specify a different container,
   * or you can disable the portal to render the content inline by passing `false`.
   */
  portal: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  /**
   * By default, modals should close with the ESC key.
   * This sets the prop on the Dialog component.
   */
  shouldCloseOnESCKeyPress: PropTypes.bool,
  /**
   * By default, modals should close when clicking outside the dialog content.
   * This sets the prop on the Dialog component.
   */
  shouldCloseOnOutsideClick: PropTypes.bool
};
Modal.defaultProps = {
  dialog: /*#__PURE__*/React.createElement(Dialog, null),
  backdrop: /*#__PURE__*/React.createElement(Mask, null),
  className: undefined,
  globalStyle: /*#__PURE__*/React.createElement(ModalGlobalStyle, null),
  isOpen: undefined,
  portal: true,
  shouldCloseOnESCKeyPress: true,
  shouldCloseOnOutsideClick: true
};